/**
 * VÉRAULT Travels - Itinerary Builder (HARDENED UX + DRAG & DROP)
 * 
 * FOUNDATIONAL RULE: Itinerary is the SINGLE SOURCE OF TRUTH
 * - AI Assistant reads from Itinerary state
 * - AI writes ONLY to itinerary upon user confirmation
 * - Client Portal renders ONLY from itinerary data
 * - Immediate sync when itinerary updates
 * 
 * Features:
 * - Card-based, sectioned input with minimal cognitive load
 * - Drag-and-drop reordering within/across time blocks and days
 * - Inline editing (no modals)
 * - Structured inputs first
 * - Visual states: Proposed (AI) / Approved / Edited
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, Check, X, ChevronDown, ChevronUp, Clock, MapPin, 
  Sparkles, Edit2, Lock, Unlock, Trash2,
  ExternalLink, Phone, DollarSign, Calendar, Sun, 
  Sunrise, Sunset, Moon, GripVertical, Copy, Save, Loader2,
  Move, ArrowUpDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { getTripStartDate } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time block configuration
const TIME_BLOCKS = {
  morning: { 
    label: 'Morning', 
    icon: Sunrise, 
    color: 'bg-amber-50 border-amber-200 text-amber-700',
    accent: 'bg-amber-100',
    times: ['7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM']
  },
  midday: { 
    label: 'Midday', 
    icon: Sun, 
    color: 'bg-sky-50 border-sky-200 text-sky-700',
    accent: 'bg-sky-100',
    times: ['12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM']
  },
  afternoon: { 
    label: 'Afternoon', 
    icon: Sunset, 
    color: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    accent: 'bg-emerald-100',
    times: ['2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM']
  },
  evening: { 
    label: 'Evening', 
    icon: Moon, 
    color: 'bg-violet-50 border-violet-200 text-violet-700',
    accent: 'bg-violet-100',
    times: ['6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM']
  }
};

// Item type configuration
const ITEM_TYPES = {
  activity: { label: 'Activity', icon: '🎯', color: 'text-blue-600' },
  restaurant: { label: 'Restaurant', icon: '🍽️', color: 'text-orange-600' },
  hotel: { label: 'Hotel', icon: '🏨', color: 'text-purple-600' },
  experience: { label: 'Experience', icon: '✨', color: 'text-pink-600' },
  transfer: { label: 'Transfer', icon: '🚗', color: 'text-gray-600' },
  free_time: { label: 'Free Time', icon: '☕', color: 'text-green-600' },
};

// Visual state styles
const ITEM_STATES = {
  proposed: { 
    label: 'AI Proposed', 
    badge: 'bg-[#C8A85A]/10 text-[#C8A85A] border-[#C8A85A]/30',
    ring: 'ring-[#C8A85A]/20'
  },
  approved: { 
    label: 'Approved', 
    badge: 'bg-[#8DA399]/10 text-[#8DA399] border-[#8DA399]/30',
    ring: 'ring-[#8DA399]/20'
  },
  edited: { 
    label: 'Edited', 
    badge: 'bg-[#5B9BD5]/10 text-[#5B9BD5] border-[#5B9BD5]/30',
    ring: 'ring-[#5B9BD5]/20'
  }
};

// Helper: Format date for display
function formatDayDate(startDate, dayNumber) {
  if (!startDate) return 'Date TBD';
  try {
    const date = new Date(startDate);
    date.setDate(date.getDate() + (dayNumber - 1));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return 'Date TBD';
  }
}

// Helper: Generate unique container ID
function getContainerId(day, section) {
  return `day-${day}-${section}`;
}

// Helper: Parse container ID
function parseContainerId(containerId) {
  const match = containerId.match(/day-(\d+)-(\w+)/);
  if (match) {
    return { day: parseInt(match[1]), section: match[2] };
  }
  return null;
}

// ============================================================
// SORTABLE ITEM CARD - Draggable activity/restaurant/etc card
// ============================================================
function SortableItemCard({ 
  item, 
  onUpdate, 
  onDelete, 
  onApprove, 
  onDuplicate,
  isDragging = false
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id: item.id,
    data: { item, type: 'item' }
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };
  
  const itemType = ITEM_TYPES[item.type] || ITEM_TYPES.activity;
  const state = ITEM_STATES[item.status] || ITEM_STATES.proposed;
  
  useEffect(() => {
    setForm(item);
  }, [item]);
  
  const handleFieldChange = (field, value) => {
    const updated = { ...form, [field]: value, status: form.status === 'approved' ? 'edited' : form.status };
    setForm(updated);
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(item.id, form);
      setEditing(false);
      setExpanded(false);
      toast.success('Saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const handleApprove = async (e) => {
    e?.stopPropagation();
    const approvedItem = { ...form, status: 'approved' };
    setForm(approvedItem);
    await onApprove(item.id, approvedItem);
  };
  
  // COLLAPSED VIEW
  if (!expanded && !editing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`group bg-white rounded-lg border ${item.status === 'proposed' ? 'border-[#C8A85A]/40 border-dashed' : 'border-[#E6E4DE]'} 
          p-3 hover:shadow-md transition-all ${state.ring} ${item.status === 'proposed' ? 'ring-1' : ''}`}
        data-testid={`item-card-${item.id}`}
      >
        <div className="flex items-start gap-2">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="p-1 rounded hover:bg-gray-100 cursor-grab active:cursor-grabbing touch-none"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-[#D1D1D1]" />
          </button>
          
          {/* Type Icon */}
          <span className="text-lg flex-shrink-0">{itemType.icon}</span>
          
          {/* Content */}
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(true)}>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-[#1A1A1A] truncate">{item.name || 'Untitled'}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${state.badge} uppercase tracking-wide flex-shrink-0`}>
                {item.status === 'proposed' && <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />}
                {item.status === 'approved' && <Check className="w-2.5 h-2.5 inline mr-0.5" />}
                {state.label}
              </span>
            </div>
            
            <div className="flex items-center gap-3 text-xs text-[#8C8C8C]">
              {item.time && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {item.time}
                </span>
              )}
              {item.address && (
                <span className="flex items-center gap-1 truncate">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{item.address.split(',')[0]}</span>
                </span>
              )}
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {item.status === 'proposed' && (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleApprove}
                className="h-7 px-2 text-[#8DA399] hover:text-[#8DA399] hover:bg-[#8DA399]/10"
              >
                <Check className="w-3.5 h-3.5 mr-1" />
                Approve
              </Button>
            )}
            <button 
              onClick={(e) => { e.stopPropagation(); setEditing(true); setExpanded(true); }} 
              className="p-1.5 rounded hover:bg-gray-100"
              title="Edit"
            >
              <Edit2 className="w-3.5 h-3.5 text-[#8C8C8C]" />
            </button>
          </div>
          
          <ChevronDown className="w-4 h-4 text-[#D1D1D1] flex-shrink-0 cursor-pointer" onClick={() => setExpanded(true)} />
        </div>
      </div>
    );
  }
  
  // EXPANDED/EDITING VIEW
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl border-2 ${editing ? 'border-[#C8A85A]' : 'border-[#E6E4DE]'} shadow-lg overflow-hidden`}
      data-testid={`item-card-expanded-${item.id}`}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-[#F9F8F4] border-b border-[#E6E4DE] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button {...attributes} {...listeners} className="p-1 rounded hover:bg-gray-200 cursor-grab">
            <GripVertical className="w-4 h-4 text-[#8C8C8C]" />
          </button>
          <span className="text-lg">{itemType.icon}</span>
          <span className="text-sm font-medium text-[#1A1A1A]">{itemType.label}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${state.badge}`}>
            {state.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => { setForm(item); setEditing(false); setExpanded(false); }}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#1A1A1A] hover:bg-[#2C2C2C]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <button onClick={() => setExpanded(false)} className="p-1 hover:bg-gray-200 rounded">
                <ChevronUp className="w-4 h-4 text-[#8C8C8C]" />
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-4">
        {/* PRIMARY FIELDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Name *</label>
            {editing ? (
              <Input
                value={form.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="e.g., Dinner at L'Arpège"
                className="bg-[#FAFAFA]"
              />
            ) : (
              <p className="text-sm text-[#1A1A1A] font-medium py-2">{item.name || '—'}</p>
            )}
          </div>
          
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Time</label>
            {editing ? (
              <Select value={form.time || ''} onValueChange={(v) => handleFieldChange('time', v)}>
                <SelectTrigger className="bg-[#FAFAFA]">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(TIME_BLOCKS).flatMap(block => 
                    block.times.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-[#1A1A1A] py-2 flex items-center gap-1">
                <Clock className="w-4 h-4 text-[#8C8C8C]" />
                {item.time || '—'}
              </p>
            )}
          </div>
          
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Type</label>
            {editing ? (
              <Select value={form.type || 'activity'} onValueChange={(v) => handleFieldChange('type', v)}>
                <SelectTrigger className="bg-[#FAFAFA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ITEM_TYPES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span>{val.icon}</span>
                        {val.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-[#1A1A1A] py-2">{itemType.icon} {itemType.label}</p>
            )}
          </div>
          
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Duration</label>
            {editing ? (
              <Select value={form.duration || ''} onValueChange={(v) => handleFieldChange('duration', v)}>
                <SelectTrigger className="bg-[#FAFAFA]">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  {['30 min', '1 hour', '1.5 hours', '2 hours', '2.5 hours', '3 hours', 'Half day', 'Full day'].map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-[#1A1A1A] py-2">{item.duration || '—'}</p>
            )}
          </div>
        </div>
        
        {/* LOCATION FIELDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Address</label>
            {editing ? (
              <Input
                value={form.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                placeholder="Full address"
                className="bg-[#FAFAFA]"
              />
            ) : (
              <p className="text-sm text-[#1A1A1A] py-2 flex items-center gap-1">
                <MapPin className="w-4 h-4 text-[#8C8C8C] flex-shrink-0" />
                {item.address || '—'}
              </p>
            )}
          </div>
          
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Phone</label>
            {editing ? (
              <Input
                value={form.phone || ''}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                placeholder="+1 234 567 8900"
                className="bg-[#FAFAFA]"
              />
            ) : (
              item.phone && (
                <p className="text-sm text-[#1A1A1A] py-2 flex items-center gap-1">
                  <Phone className="w-4 h-4 text-[#8C8C8C]" />
                  {item.phone}
                </p>
              )
            )}
          </div>
          
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Price Range</label>
            {editing ? (
              <Select value={form.price_range || ''} onValueChange={(v) => handleFieldChange('price_range', v)}>
                <SelectTrigger className="bg-[#FAFAFA]">
                  <SelectValue placeholder="Select price" />
                </SelectTrigger>
                <SelectContent>
                  {['$', '$$', '$$$', '$$$$', 'Included', 'Complimentary'].map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              item.price_range && (
                <p className="text-sm text-[#1A1A1A] py-2 flex items-center gap-1">
                  <DollarSign className="w-4 h-4 text-[#8C8C8C]" />
                  {item.price_range}
                </p>
              )
            )}
          </div>
        </div>
        
        {/* LINKS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Website</label>
            {editing ? (
              <Input
                value={form.website_url || ''}
                onChange={(e) => handleFieldChange('website_url', e.target.value)}
                placeholder="https://..."
                className="bg-[#FAFAFA]"
              />
            ) : (
              item.website_url && (
                <a 
                  href={item.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#C8A85A] hover:underline flex items-center gap-1 py-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Website
                </a>
              )
            )}
          </div>
          
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Booking Link</label>
            {editing ? (
              <Input
                value={form.booking_url || ''}
                onChange={(e) => handleFieldChange('booking_url', e.target.value)}
                placeholder="https://..."
                className="bg-[#FAFAFA]"
              />
            ) : (
              item.booking_url && (
                <a 
                  href={item.booking_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#8DA399] hover:underline flex items-center gap-1 py-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Make Reservation
                </a>
              )
            )}
          </div>
        </div>
        
        {/* NOTES */}
        <div>
          <label className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-1 block">Notes (optional)</label>
          {editing ? (
            <Textarea
              value={form.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              placeholder="Any additional details..."
              className="bg-[#FAFAFA] min-h-[60px]"
            />
          ) : (
            item.notes && (
              <p className="text-sm text-[#595959] py-2 whitespace-pre-wrap">{item.notes}</p>
            )
          )}
        </div>
        
        {/* AI Source indicator */}
        {item.added_by === 'Trip AI' && (
          <div className="flex items-center gap-2 text-xs text-[#C8A85A] bg-[#C8A85A]/5 px-3 py-2 rounded-lg">
            <Sparkles className="w-3.5 h-3.5" />
            Added by Trip AI • {item.source_verified ? 'Source verified' : 'Pending verification'}
          </div>
        )}
        
        {/* ACTIONS */}
        <div className="flex items-center justify-between pt-4 border-t border-[#E6E4DE]">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => onDuplicate(item)}
              className="p-2 rounded-lg hover:bg-gray-100 text-[#8C8C8C]"
              title="Duplicate"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button 
              onClick={() => onDelete(item.id)}
              className="p-2 rounded-lg hover:bg-red-50 text-[#B37E6B]"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          
          {item.status === 'proposed' && !editing && (
            <Button onClick={handleApprove} className="bg-[#8DA399] hover:bg-[#7A9389]">
              <Check className="w-4 h-4 mr-2" />
              Approve Item
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DRAG OVERLAY ITEM - Shows while dragging
// ============================================================
function DragOverlayItem({ item }) {
  const itemType = ITEM_TYPES[item.type] || ITEM_TYPES.activity;
  const state = ITEM_STATES[item.status] || ITEM_STATES.proposed;
  
  return (
    <div className="bg-white rounded-lg border-2 border-[#C8A85A] shadow-2xl p-3 w-80 opacity-95">
      <div className="flex items-start gap-3">
        <Move className="w-4 h-4 text-[#C8A85A]" />
        <span className="text-lg">{itemType.icon}</span>
        <div className="flex-1">
          <span className="font-medium text-[#1A1A1A]">{item.name || 'Untitled'}</span>
          <p className="text-xs text-[#8C8C8C]">Drag to reorder or move to another section</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DROPPABLE TIME BLOCK SECTION
// ============================================================
function DroppableTimeBlock({ 
  block, 
  blockKey, 
  dayNumber,
  items, 
  onAddItem, 
  onUpdateItem, 
  onDeleteItem, 
  onApproveItem, 
  onDuplicateItem 
}) {
  const Icon = block.icon;
  const containerId = getContainerId(dayNumber, blockKey);
  
  const { setNodeRef, isOver } = useDroppable({
    id: containerId,
    data: { day: dayNumber, section: blockKey, type: 'container' }
  });
  
  const blockItems = items.filter(i => i.day === dayNumber && i.section === blockKey).sort((a, b) => {
    if (a.time && b.time) return a.time.localeCompare(b.time);
    return (a.order || 0) - (b.order || 0);
  });
  
  const itemIds = blockItems.map(i => i.id);
  const hasProposed = blockItems.some(i => i.status === 'proposed');
  
  return (
    <div 
      className={`rounded-xl border ${block.color} overflow-hidden transition-all ${isOver ? 'ring-2 ring-[#C8A85A] ring-offset-2' : ''}`}
      data-testid={`time-block-${dayNumber}-${blockKey}`}
    >
      {/* Block Header */}
      <div className={`px-4 py-2.5 ${block.accent} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="text-sm font-medium">{block.label}</span>
          <span className="text-xs opacity-60">({blockItems.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {hasProposed && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C8A85A]/20 text-[#C8A85A]">
              Has proposals
            </span>
          )}
          <button
            onClick={() => onAddItem(dayNumber, blockKey)}
            className="p-1.5 rounded-lg hover:bg-white/50 transition-colors"
            title={`Add to ${block.label}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Block Items - Droppable */}
      <div 
        ref={setNodeRef}
        className={`p-3 space-y-2 min-h-[60px] bg-white/50 transition-colors ${isOver ? 'bg-[#C8A85A]/5' : ''}`}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <AnimatePresence mode="popLayout">
            {blockItems.map(item => (
              <SortableItemCard
                key={item.id}
                item={item}
                onUpdate={onUpdateItem}
                onDelete={onDeleteItem}
                onApprove={onApproveItem}
                onDuplicate={onDuplicateItem}
              />
            ))}
          </AnimatePresence>
        </SortableContext>
        
        {blockItems.length === 0 && (
          <button 
            onClick={() => onAddItem(dayNumber, blockKey)}
            className="w-full py-4 border-2 border-dashed border-current/20 rounded-lg text-sm opacity-50 hover:opacity-75 transition-opacity flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add {block.label.toLowerCase()} activity
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// DAY CARD
// ============================================================
function DayCard({ 
  dayNumber, 
  totalDays,
  startDate,
  items, 
  isLocked,
  onLockDay,
  onUnlockDay,
  onApproveDay,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onApproveItem,
  onDuplicateItem,
  onOptimizeRoute
}) {
  const [collapsed, setCollapsed] = useState(false);
  
  const dayItems = items.filter(i => i.day === dayNumber);
  const proposedCount = dayItems.filter(i => i.status === 'proposed').length;
  const approvedCount = dayItems.filter(i => i.status === 'approved').length;
  const formattedDate = formatDayDate(startDate, dayNumber);
  
  return (
    <motion.div
      layout
      className={`bg-white rounded-2xl border ${isLocked ? 'border-[#8DA399]/40' : 'border-[#E6E4DE]'} shadow-sm overflow-hidden`}
      data-testid={`day-card-${dayNumber}`}
    >
      {/* Day Header */}
      <div className={`px-5 py-4 ${isLocked ? 'bg-[#8DA399]/5' : 'bg-[#F9F8F4]'} border-b border-[#E6E4DE]`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-serif ${
              isLocked ? 'bg-[#8DA399] text-white' : 'bg-[#1A1A1A] text-white'
            }`}>
              {dayNumber}
            </div>
            
            <div>
              <h3 className="font-serif text-lg text-[#1A1A1A]">Day {dayNumber}</h3>
              <p className="text-sm text-[#8C8C8C]">{formattedDate}</p>
            </div>
            
            {isLocked && (
              <span className="flex items-center gap-1 text-xs text-[#8DA399] bg-[#8DA399]/10 px-2 py-1 rounded-full">
                <Lock className="w-3 h-3" />
                Locked
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="text-xs text-[#8C8C8C] mr-2">
              {approvedCount}/{dayItems.length} approved
            </div>
            
            {/* P1 ENHANCEMENT: Optimize Route Button */}
            {dayItems.length >= 2 && !isLocked && (
              <Button 
                size="sm"
                variant="outline"
                onClick={() => onOptimizeRoute(dayNumber)}
                className="border-[#C8A85A] text-[#C8A85A] hover:bg-[#C8A85A]/10"
                data-testid={`optimize-route-btn-day-${dayNumber}`}
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                Optimize
              </Button>
            )}
            
            {proposedCount > 0 && !isLocked && (
              <Button 
                size="sm" 
                onClick={() => onApproveDay(dayNumber)}
                className="bg-[#8DA399] hover:bg-[#7A9389]"
              >
                <Check className="w-4 h-4 mr-1" />
                Approve Day ({proposedCount})
              </Button>
            )}
            
            <button
              onClick={() => isLocked ? onUnlockDay(dayNumber) : onLockDay(dayNumber)}
              className={`p-2 rounded-lg transition-colors ${
                isLocked ? 'text-[#8DA399] hover:bg-[#8DA399]/10' : 'text-[#8C8C8C] hover:bg-gray-100'
              }`}
              title={isLocked ? 'Unlock day' : 'Lock day'}
            >
              {isLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            </button>
            
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-2 rounded-lg hover:bg-gray-100 text-[#8C8C8C]"
            >
              {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Day Content */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="p-4 space-y-4"
          >
            {Object.entries(TIME_BLOCKS).map(([key, block]) => (
              <DroppableTimeBlock
                key={key}
                block={block}
                blockKey={key}
                dayNumber={dayNumber}
                items={items}
                onAddItem={onAddItem}
                onUpdateItem={onUpdateItem}
                onDeleteItem={onDeleteItem}
                onApproveItem={onApproveItem}
                onDuplicateItem={onDuplicateItem}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================================
// MAIN ITINERARY BUILDER COMPONENT
// ============================================================
export function ItineraryBuilder({ tripId, trip, planningState, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  
  const totalDays = planningState?.total_days || 7;
  const lockedDays = planningState?.locked_days || {};
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  
  // Load itinerary items
  useEffect(() => {
    if (tripId) {
      loadItems();
    }
  }, [tripId]);
  
  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/travels/trip-itinerary/${tripId}`);
      const days = res.data || [];
      
      const allItems = [];
      days.forEach((day, idx) => {
        const dayNumber = day.day_number || idx + 1;
        ['morning', 'midday', 'afternoon', 'evening'].forEach(section => {
          const sectionItems = day[section] || [];
          sectionItems.forEach((item, order) => {
            allItems.push({
              ...item,
              id: item.id || `${dayNumber}-${section}-${order}`,
              day: dayNumber,
              section: section,
              order: order,
              status: item.status || (item.added_by === 'Trip AI' ? 'proposed' : 'approved')
            });
          });
        });
      });
      
      setItems(allItems);
    } catch (e) {
      console.error('Failed to load itinerary:', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  
  const saveItems = useCallback(async (updatedItems) => {
    setSaving(true);
    try {
      const daysMap = {};
      updatedItems.forEach(item => {
        const dayNum = item.day || 1;
        if (!daysMap[dayNum]) {
          daysMap[dayNum] = { day_number: dayNum, morning: [], midday: [], afternoon: [], evening: [] };
        }
        const section = item.section || 'afternoon';
        if (!daysMap[dayNum][section]) {
          daysMap[dayNum][section] = [];
        }
        daysMap[dayNum][section].push(item);
      });
      
      const days = Object.values(daysMap).sort((a, b) => a.day_number - b.day_number);
      
      await axios.put(`${API}/travels/itinerary/${tripId}`, { days });
      setItems(updatedItems);
      
      // FOUNDATIONAL RULE: Notify that itinerary updated (for AI context sync)
      window.dispatchEvent(new CustomEvent('itinerary-updated', { detail: { tripId, days } }));
      
    } catch (e) {
      console.error('Failed to save itinerary:', e);
      toast.error('Failed to save');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [tripId]);
  
  // DnD handlers
  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveItem(items.find(i => i.id === active.id));
  }, [items]);
  
  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    if (activeId === overId) return;
    
    const activeItem = items.find(i => i.id === activeId);
    if (!activeItem) return;
    
    // Check if dropping on a container (time block)
    const overData = over.data.current;
    if (overData?.type === 'container') {
      const { day, section } = overData;
      
      // Move item to new container
      if (activeItem.day !== day || activeItem.section !== section) {
        setItems(prev => prev.map(item => 
          item.id === activeId 
            ? { ...item, day, section, status: item.status === 'approved' ? 'edited' : item.status }
            : item
        ));
      }
    }
  }, [items]);
  
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    setActiveItem(null);
    
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    const activeItem = items.find(i => i.id === activeId);
    if (!activeItem) return;
    
    const overData = over.data.current;
    
    // Handle dropping on a container
    if (overData?.type === 'container') {
      const { day, section } = overData;
      const updatedItems = items.map(item => 
        item.id === activeId 
          ? { ...item, day, section, status: item.status === 'approved' ? 'edited' : item.status }
          : item
      );
      await saveItems(updatedItems);
      toast.success(`Moved to Day ${day} - ${TIME_BLOCKS[section]?.label || section}`);
      return;
    }
    
    // Handle reordering within same container
    const overItem = items.find(i => i.id === overId);
    if (!overItem) return;
    
    if (activeItem.day === overItem.day && activeItem.section === overItem.section) {
      const containerItems = items
        .filter(i => i.day === activeItem.day && i.section === activeItem.section)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      const oldIndex = containerItems.findIndex(i => i.id === activeId);
      const newIndex = containerItems.findIndex(i => i.id === overId);
      
      if (oldIndex !== newIndex) {
        const reordered = arrayMove(containerItems, oldIndex, newIndex);
        const updatedItems = items.map(item => {
          if (item.day === activeItem.day && item.section === activeItem.section) {
            const newOrder = reordered.findIndex(r => r.id === item.id);
            return { ...item, order: newOrder };
          }
          return item;
        });
        await saveItems(updatedItems);
      }
    } else {
      // Moving to different container via dropping on an item
      const updatedItems = items.map(item => 
        item.id === activeId 
          ? { ...item, day: overItem.day, section: overItem.section, status: item.status === 'approved' ? 'edited' : item.status }
          : item
      );
      await saveItems(updatedItems);
      toast.success(`Moved to Day ${overItem.day} - ${TIME_BLOCKS[overItem.section]?.label || overItem.section}`);
    }
  }, [items, saveItems]);
  
  // ITEM OPERATIONS
  const handleAddItem = useCallback(async (dayNumber, section) => {
    const newItem = {
      id: `new-${Date.now()}`,
      day: dayNumber,
      section: section,
      name: '',
      type: 'activity',
      status: 'edited',
      time: '',
      address: '',
      order: items.filter(i => i.day === dayNumber && i.section === section).length
    };
    
    setItems(prev => [...prev, newItem]);
  }, [items]);
  
  const handleUpdateItem = useCallback(async (itemId, updates) => {
    const updated = items.map(i => 
      i.id === itemId ? { ...i, ...updates } : i
    );
    await saveItems(updated);
  }, [items, saveItems]);
  
  const handleDeleteItem = useCallback(async (itemId) => {
    if (!confirm('Delete this item?')) return;
    const updated = items.filter(i => i.id !== itemId);
    await saveItems(updated);
    toast.success('Item deleted');
  }, [items, saveItems]);
  
  const handleApproveItem = useCallback(async (itemId, approvedData) => {
    const updated = items.map(i => 
      i.id === itemId ? { ...approvedData, status: 'approved' } : i
    );
    await saveItems(updated);
    toast.success('Item approved');
  }, [items, saveItems]);
  
  const handleDuplicateItem = useCallback(async (item) => {
    const duplicate = {
      ...item,
      id: `dup-${Date.now()}`,
      name: `${item.name} (copy)`,
      status: 'edited'
    };
    const updated = [...items, duplicate];
    await saveItems(updated);
    toast.success('Item duplicated');
  }, [items, saveItems]);
  
  // DAY OPERATIONS
  const handleApproveDay = useCallback(async (dayNumber) => {
    const updated = items.map(i => 
      i.day === dayNumber && i.status === 'proposed' 
        ? { ...i, status: 'approved' } 
        : i
    );
    await saveItems(updated);
    
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/apply-day`, { day_number: dayNumber });
    } catch (e) {
      console.error('Failed to lock day:', e);
    }
    
    toast.success(`Day ${dayNumber} approved`);
    onRefresh?.();
  }, [items, saveItems, tripId, onRefresh]);
  
  const handleLockDay = useCallback(async (dayNumber) => {
    toast.success(`Day ${dayNumber} locked`);
    onRefresh?.();
  }, [onRefresh]);
  
  const handleUnlockDay = useCallback(async (dayNumber) => {
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-planning-state/unlock-day/${dayNumber}`);
      toast.success(`Day ${dayNumber} unlocked`);
      onRefresh?.();
    } catch (e) {
      toast.error('Failed to unlock day');
    }
  }, [tripId, onRefresh]);
  
  // ROUTE OPTIMIZATION STATE
  const [optimizing, setOptimizing] = useState(null);
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);
  
  // P1 ENHANCEMENT: Route Optimization Handler
  const handleOptimizeRoute = useCallback(async (dayNumber) => {
    setOptimizing(dayNumber);
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/ai/optimize-day-route`, {
        day_number: dayNumber,
        optimize_for: 'efficiency'
      });
      
      if (res.data.success && res.data.optimization?.optimized_order) {
        setOptimizationResult({
          dayNumber,
          currentItems: res.data.current_items,
          optimization: res.data.optimization
        });
        setShowOptimizationModal(true);
      } else if (res.data.optimized === false) {
        toast.info(res.data.message || 'Day has fewer than 2 items - no optimization needed');
      } else {
        toast.info('AI analyzed the route but no reordering needed');
        console.log('Optimization result:', res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to optimize route');
      console.error('Route optimization error:', e);
    } finally {
      setOptimizing(null);
    }
  }, [tripId]);
  
  // Apply optimization result
  const handleApplyOptimization = useCallback(async () => {
    if (!optimizationResult) return;
    
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/ai/apply-route-optimization`, {
        day_number: optimizationResult.dayNumber,
        optimized_order: optimizationResult.optimization.optimized_order
      });
      
      if (res.data.success) {
        toast.success(`Day ${optimizationResult.dayNumber} route optimized! ${res.data.items_reordered} items reordered.`);
        loadItems(); // Refresh itinerary
        setShowOptimizationModal(false);
        setOptimizationResult(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply optimization');
    }
  }, [optimizationResult, tripId, loadItems]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8A85A]" />
      </div>
    );
  }
  
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6" data-testid="itinerary-builder">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Itinerary Builder</h2>
            <p className="text-sm text-[#8C8C8C]">
              {totalDays} days • {items.filter(i => i.status === 'approved').length}/{items.length} items approved
              <span className="ml-2 text-[#C8A85A]">
                <ArrowUpDown className="w-3 h-3 inline mr-1" />
                Drag items to reorder
              </span>
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {saving && (
              <span className="text-xs text-[#8C8C8C] flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </span>
            )}
            <Button variant="outline" size="sm" onClick={loadItems}>
              Refresh
            </Button>
          </div>
        </div>
        
        {/* Day Cards */}
        <div className="space-y-6">
          {Array.from({ length: totalDays }, (_, i) => i + 1).map(dayNum => (
            <DayCard
              key={dayNum}
              dayNumber={dayNum}
              totalDays={totalDays}
              startDate={getTripStartDate(trip)}
              items={items}
              isLocked={lockedDays[String(dayNum)] || lockedDays[dayNum]}
              onLockDay={handleLockDay}
              onUnlockDay={handleUnlockDay}
              onApproveDay={handleApproveDay}
              onAddItem={handleAddItem}
              onUpdateItem={handleUpdateItem}
              onDeleteItem={handleDeleteItem}
              onApproveItem={handleApproveItem}
              onDuplicateItem={handleDuplicateItem}
              onOptimizeRoute={handleOptimizeRoute}
            />
          ))}
        </div>
        
        {/* Empty State */}
        {totalDays === 0 && (
          <div className="text-center py-20 bg-[#F9F8F4] rounded-2xl">
            <Calendar className="w-12 h-12 text-[#C8A85A]/30 mx-auto mb-4" />
            <h3 className="font-medium text-[#1A1A1A] mb-2">No trip dates set</h3>
            <p className="text-sm text-[#8C8C8C]">Set the trip dates to start building the itinerary</p>
          </div>
        )}
      </div>
      
      {/* Drag Overlay */}
      <DragOverlay>
        {activeItem ? <DragOverlayItem item={activeItem} /> : null}
      </DragOverlay>
      
      {/* P1 ENHANCEMENT: Route Optimization Modal */}
      <AnimatePresence>
        {showOptimizationModal && optimizationResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowOptimizationModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
              data-testid="route-optimization-modal"
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E6E4DE] bg-[#F9F8F4]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C8A85A] flex items-center justify-center">
                      <ArrowUpDown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-[#1A1A1A]">Optimized Route</h3>
                      <p className="text-sm text-[#8C8C8C]">Day {optimizationResult.dayNumber}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowOptimizationModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5 text-[#8C8C8C]" />
                  </button>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="px-6 py-4 overflow-y-auto max-h-[50vh]">
                {/* Optimization Notes */}
                {optimizationResult.optimization.optimization_notes && (
                  <div className="mb-4 p-3 bg-[#C8A85A]/10 rounded-lg">
                    <p className="text-sm text-[#1A1A1A]">
                      <Sparkles className="w-4 h-4 inline mr-2 text-[#C8A85A]" />
                      {optimizationResult.optimization.optimization_notes}
                    </p>
                  </div>
                )}
                
                {/* Estimated Benefit */}
                {optimizationResult.optimization.estimated_benefit && (
                  <p className="text-sm text-[#8DA399] mb-4">
                    <Check className="w-4 h-4 inline mr-1" />
                    {optimizationResult.optimization.estimated_benefit}
                  </p>
                )}
                
                {/* Optimized Order List */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-[#8C8C8C] uppercase tracking-wide mb-3">
                    Suggested Order
                  </p>
                  {optimizationResult.optimization.optimized_order?.map((item, idx) => (
                    <div 
                      key={idx}
                      className="flex items-start gap-3 p-3 bg-[#F9F8F4] rounded-lg"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {item.position || idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A1A1A] truncate">
                          {item.item_title}
                        </p>
                        <p className="text-xs text-[#8C8C8C]">
                          {TIME_BLOCKS[item.suggested_time_block]?.label || item.suggested_time_block}
                        </p>
                        {item.reason && (
                          <p className="text-xs text-[#595959] mt-1">
                            {item.reason}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E6E4DE] flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowOptimizationModal(false)}
                >
                  Keep Current Order
                </Button>
                <Button 
                  onClick={handleApplyOptimization}
                  className="bg-[#C8A85A] hover:bg-[#B39049] text-white"
                  data-testid="apply-optimization-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Apply Optimization
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DndContext>
  );
}

export default ItineraryBuilder;
